import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, GlobalStyles, IconButton, Link, Typography } from "@mui/material";

const IOS_URL =
  "https://apps.apple.com/us/app/trainer-app-coaching-platform/id6743857294";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.trainer.app.trainer.app";
const STORAGE_KEY = "trainerAppDownloadBannerDismissed";

/** Set to `false` before release. Shows banner on every device and skips dismiss persistence. */
const DEBUG_ALWAYS_SHOW_APP_DOWNLOAD_BANNER = false;

function getMobileStoreTarget() {
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // Chrome / Edge DevTools: device toolbar sets navigator.userAgentData.mobile even when the
  // reduced User-Agent string still looks like desktop (so iPhone regex never matches).
  const clientHintsMobile =
    typeof navigator !== "undefined" &&
    navigator.userAgentData?.mobile === true;

  if (isAndroid) {
    return {
      href: ANDROID_URL,
      label: "Get it on Google Play",
    };
  }
  if (isIOS || (clientHintsMobile && !isAndroid)) {
    return {
      href: IOS_URL,
      label: "Download on the App Store",
    };
  }
  if (DEBUG_ALWAYS_SHOW_APP_DOWNLOAD_BANNER) {
    return {
      href: IOS_URL,
      label: "Download on the App Store",
    };
  }
  return null;
}

const bannerSx = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  /* Anchor to safe area so the bar isn’t drawn under the home indicator / outside the visible frame */
  pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  py: 1.5,
  pl: 2,
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "100%",
  bgcolor: "#111",
  borderTop: "1px solid #333",
  boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.35)",
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: 15,
  /* Promotes correct compositing with mobile browser chrome */
  WebkitTransform: "translateZ(0)",
  transform: "translateZ(0)",
};

const contentSx = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
};

const hintSx = {
  m: 0,
  color: "#fff",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.3,
};

const storeLinkSx = {
  color: "red",
  textDecoration: "none",
  fontWeight: 600,
  "&:active": { opacity: 0.85 },
};

const dismissSx = {
  flexShrink: 0,
  width: 36,
  height: 36,
  color: "#fff",
  "&:hover": {
    color: "#ccc",
    bgcolor: "rgba(255, 255, 255, 0.08)",
  },
  "&:focus-visible": {
    outline: "none",
  },
};

const AppDownloadBanner = () => {
  const [target, setTarget] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = getMobileStoreTarget();
    if (!t) return;
    setTarget(t);
    if (!DEBUG_ALWAYS_SHOW_APP_DOWNLOAD_BANNER) {
      try {
        if (localStorage.getItem(STORAGE_KEY) === "1") {
          setDismissed(true);
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  const visible = Boolean(target && !dismissed);

  if (!visible) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (DEBUG_ALWAYS_SHOW_APP_DOWNLOAD_BANNER) return;
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            paddingBottom: "calc(72px + env(safe-area-inset-bottom, 0px))",
            boxSizing: "border-box",
          },
        }}
      />
      <Box sx={bannerSx}>
        <Box sx={contentSx}>
          <Typography component="p" sx={hintSx}>
            Use the app for the best experience.
          </Typography>
          <Link
            href={target.href}
            rel="noopener noreferrer"
            underline="none"
            sx={storeLinkSx}
          >
            {target.label}
          </Link>
        </Box>
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss"
          sx={dismissSx}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
    </>
  );
};

export default AppDownloadBanner;
